import React from "react";
import { Button, Badge } from "react-bootstrap";
import moment from "moment";
import { MdAddChart } from "react-icons/md";
import { TfiViewListAlt } from "react-icons/tfi";
import { FaEdit } from "react-icons/fa";

const ProjectBuildingBlockTab = ({
  // Data
  buildingBlocks,
  blocksLoading,
  summaryLoading,
  blockViewType,
  blockSummaries,

  // Permissions
  hasProjectAdminRole,
  hasProjectManagerRole,

  // Handlers
  setBlockViewType,
  setShowBlockModal,
  openMapModal,
  openBlockItemsModal,
  handleEditBlock,
  getUsernameFromId,
}) => {
  const canEdit = hasProjectAdminRole() || hasProjectManagerRole();

  return (
    <div className="project-view-fade-in">
      <div
        className="d-flex justify-content-between align-items-center mb-3"
        style={{ padding: "20px" }}
      >
        <h4>Building Blocks</h4>

        <div className="d-flex gap-2">
          {/* Toggle - Visible to all roles */}
          <Button
            size="sm"
            variant={blockViewType === "TASK" ? "primary" : "outline-primary"}
            onClick={() => setBlockViewType("TASK")}
          >
            Tasks
          </Button>

          <Button
            size="sm"
            variant={blockViewType === "TICKET" ? "primary" : "outline-primary"}
            onClick={() => setBlockViewType("TICKET")}
          >
            Tickets
          </Button>

          {/* Add Building Block Button - Only for Admin and Manager */}
          {canEdit && (
            <Button onClick={() => setShowBlockModal(true)}>
              + Add Building Block
            </Button>
          )}
        </div>
      </div>

      {blocksLoading || summaryLoading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2">Loading {blockViewType} summary...</p>
        </div>
      ) : buildingBlocks.length === 0 ? (
        <p className="text-muted">No building blocks found</p>
      ) : (
        <div className="table-responsive" style={{ margin: "0px 14px" }}>
          <table
            className="table table-bordered table-hover align-middle"
            style={{ width: "100vw" }}
          >
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Block ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Type</th>
                <th>Process POC</th>
                <th>Tech POC</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Total</th>

                {blockViewType === "TASK" ? (
                  <>
                    <th>New</th>
                    <th>In Progress</th>
                    <th>Not Started</th>
                    <th>On Hold</th>
                    <th>Completed</th>
                    <th>Cancelled</th>
                  </>
                ) : (
                  <>
                    <th>Open</th>
                    <th>In Progress</th>
                    <th>Resolved</th>
                    <th>Closed</th>
                  </>
                )}

                <th>Progress</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {buildingBlocks.map((block, index) => (
                <tr key={block.buildingBlockId}>
                  <td>{index + 1}</td>
                  <td>{block.buildingBlockId}</td>
                  <td>{block.buildingBlockName}</td>
                  <td>
                    <span
                      className={`badge ${
                        block.buildingBlockStatus === "Completed"
                          ? "bg-success"
                          : block.buildingBlockStatus === "In Progress"
                            ? "bg-primary"
                            : "bg-secondary"
                      }`}
                    >
                      {block.buildingBlockStatus || "N/A"}
                    </span>
                  </td>
                  <td>{block.buildingBlockTypes || "-"}</td>
                  <td>
                    {getUsernameFromId(block.buildingBlockProcessPoc) || "-"}
                  </td>
                  <td>
                    {getUsernameFromId(block.buildingBlockTechPoc) || "-"}
                  </td>
                  <td>
                    {block.actualStartDate
                      ? moment(block.actualStartDate).format("MMM DD, YYYY")
                      : "-"}
                  </td>
                  <td>
                    {block.actualEndDate
                      ? moment(block.actualEndDate).format("MMM DD, YYYY")
                      : "-"}
                  </td>
                  <td>{blockSummaries[block.buildingBlockId]?.total || 0}</td>

                  {blockViewType === "TASK" ? (
                    <>
                      <td>{blockSummaries[block.buildingBlockId]?.new || 0}</td>
                      <td>
                        {blockSummaries[block.buildingBlockId]?.inProgress || 0}
                      </td>
                      <td>
                        {blockSummaries[block.buildingBlockId]?.notStarted || 0}
                      </td>
                      <td>
                        {blockSummaries[block.buildingBlockId]?.onHold || 0}
                      </td>
                      <td>
                        {blockSummaries[block.buildingBlockId]?.completed || 0}
                      </td>
                      <td>
                        {blockSummaries[block.buildingBlockId]?.cancelled || 0}
                      </td>
                    </>
                  ) : (
                    <>
                      <td>
                        {blockSummaries[block.buildingBlockId]?.open || 0}
                      </td>
                      <td>
                        {blockSummaries[block.buildingBlockId]?.inProgress || 0}
                      </td>
                      <td>
                        {blockSummaries[block.buildingBlockId]?.resolved || 0}
                      </td>
                      <td>
                        {blockSummaries[block.buildingBlockId]?.closed || 0}
                      </td>
                    </>
                  )}

                  <td>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className="progress-bar bg-success"
                        style={{
                          width: `${blockSummaries[block.buildingBlockId]?.progressPercent || 0}%`,
                        }}
                      ></div>
                    </div>
                    <small>
                      {blockSummaries[block.buildingBlockId]?.progressPercent ||
                        0}
                      %
                    </small>
                  </td>

                  <td style={{ display: "flex", gap: "6px" }}>
                    {/* Map Button - Only for Admin and Manager */}
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => openMapModal(block)}
                        style={{ padding: "10px" }}
                        title="Map items to this block"
                      >
                        <MdAddChart style={{ fontSize: "16px" }} />
                      </Button>
                    )}

                    {/* View Button - Visible to all roles */}
                    <Button
                      size="sm"
                      variant="info"
                      onClick={() => openBlockItemsModal(block)}
                      style={{ padding: "10px" }}
                      title="View block items"
                    >
                      <TfiViewListAlt style={{ fontSize: "16px" }} />
                    </Button>

                    {/* Edit Button - Only for Admin and Manager */}
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="warning"
                        onClick={() => handleEditBlock(block)}
                        style={{ padding: "10px" }}
                        title="Edit block"
                      >
                        <FaEdit style={{ fontSize: "16px" }} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectBuildingBlockTab;
